import { filterToQueryParams, transformSorting } from '../utils';
import { request } from '../request';
import { Filter, SortingModel } from '@libs/store/filters';
import { PaginatedResponse } from '@libs/models/common';
import { StaffListItem } from '@libs/models/talents/talent';
import dayjs from 'dayjs';

export type GetTalentsParams = {
  search?: string;
  filters?: Record<string, Filter>;
  orderBy?: SortingModel;
};
export async function getAllTalents(params?: GetTalentsParams): Promise<StaffListItem[]> {
  const filters = filterToQueryParams(params?.filters ?? {}, {
    removeEmpty: true,
    renameMap: {
      specialties: 'specialities',
      status: 'staffingStatuses',
      qualifications: 'nurseQualifications',
      staffingTypes: 'staffingType',
      locations: 'locations',
      search: 'search',
    },
  });
  const sorting = transformSorting(params?.orderBy, {
    renameMap: {
      name: 'fullName',
      email: 'email',
      phone: 'phoneNumber',
      status: 'staffingStatus',
      lastWorkedTime: 'lastWorkedTime',
      qualifications: 'nursequalification_abbreviation',
      specialties: 'specialty_name',
    },
  });
  const response = await request.get<PaginatedResponse<AllTalentsResponseItem[]>>('/v2/staff', {
    params: {
      search: params?.search || '',
      orderBy_in: sorting,
      ...filters,
      pageNumber: -1,
      pageSize: 0,
    },
  });
  return response.data.items.map<StaffListItem>((item) => ({
    ...item,
    id: item.id,
    firstName: item.firstName,
    lastName: item.lastName,
    fullName: [item.firstName, item.lastName].filter(Boolean).join(' '),
    lastWorkedTime: item.lastWorkedTime ? dayjs(item.lastWorkedTime) : dayjs(),
    address: item.firstAddress,
    status: item.staffingStatus,
    qualifications: item.nurseQualifications.map((qualification) => ({
      ...qualification,
    })),
    orientedList: (item.orientedRecords ?? []).map((o) => ({
      locationId: o.locationId,
      value: o.oriented === 'Yes',
    })),
    credentials: item.credentials
      ? item.credentials.map((c) => ({
          ...c,
          type: c.type,
          name: c.fileName,
          url: c.filePath,
        }))
      : [],
    specialties: item.specialities.map((specialty) => ({
      ...specialty,
    })),
  }));
}

export type AllTalentsResponseItem = {
  id: string; // 'staffmember-01k36pcpknqbq67ycc97dpgha2';
  staffingType: 'Internal';
  imageUrl: string; // 'https://www.samplestaff.com/staff/id';
  firstName: string; // 'FirstName';
  lastName: string; //' LastName';
  phoneNumber: string; // '+1 (123) 456-7890';
  email: string; // 'sample@gmail.com';
  firstAddress: {
    firstAddress: string; // 'FirstAddress';
    city: string; // 'City';
    state: string; // 'State';
    zipCode: string; // '11111';
    secondAddress: string; // 'SecondAddress';
  };
  staffingStatus: 'Active' | 'Deactivated';
  lastWorkedTime: string; // '2025-08-21T16:11:33.8775861Z';
  agency?: {
    id: string; // 'agency-01k36pcpknryraw93tk6jbrzr0';
    agencyTenantId: string; // 'Agency Name';
    logoUrl: string; // 'https://www.samplestaff.com/agency/logo';
    name: string; // 'Agency Name';
  };
  nurseQualifications: [
    {
      id: string; // 'nursequalification-01k36pcpknryraw93tk6jbrzr0';
      name: string; // 'Name';
      abbreviation: string; // 'Abbreviation';
      category: string; // 'Category';
    },
  ];
  orientedRecords: {
    locationId: string; // 'location-01k6rvfdgyqz3772fdfgz3ht5f';
    oriented: string; // 'Yes';
  }[];
  specialities: [
    {
      id: string; // 'speciality-01k36pcpknjx3x287fprqp3mf0';
      name: string; // 'Name';
      category: string; //  'Category';
    },
  ];
  credentials: {
    type: 'License' | 'Certification' | 'BackgroundCheck' | 'HealthRecord' | 'Assessment' | 'EDocument';
    fileName: string; //  'File Name.pdf'; - any human-readable name (not the physical file name)
    filePath: string; //  'https://example.com/file.pdf';
  }[];
};
